import { useState } from 'react';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import {
    PROPERTY_TYPE_OPTIONS,
    AREA_UNIT_OPTIONS,
    LEGAL_REGIME_OPTIONS,
    COST_TYPE_OPTIONS,
    PropertyType,
    MAX_OWNERS,
} from '../../../../types/Property';
import type {
    PropertyType as PropertyTypeValue,
    AreaUnit as AreaUnitValue,
    LegalRegime as LegalRegimeValue,
    PropertyOwner,
    PropertyCost,
} from '../../../../types/Property';
import type { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

const UF_OPTIONS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

interface PropertyValuationInput {
    forcedLiquidationValue: number;
    valuationDate: string;
}

interface PropertyFormProps {
    isCreating: boolean;
    errorFields: ErrorField[];
    name: string; setName: (v: string) => void;
    registrationNumber: string; setRegistrationNumber: (v: string) => void;
    registryOffice: string; setRegistryOffice: (v: string) => void;
    registryState: string; setRegistryState: (v: string) => void;
    propertyType: PropertyTypeValue; setPropertyType: (v: PropertyTypeValue) => void;
    incraCode: string; setIncraCode: (v: string) => void;
    carCode: string; setCarCode: (v: string) => void;
    location: string; setLocation: (v: string) => void;
    areaValue: number | ''; setAreaValue: (v: number | '') => void;
    areaUnit: AreaUnitValue; setAreaUnit: (v: AreaUnitValue) => void;
    legalRegime: LegalRegimeValue; setLegalRegime: (v: LegalRegimeValue) => void;
    appraised: boolean; setAppraised: (v: boolean) => void;
    monitoringEnabled: boolean; setMonitoringEnabled: (v: boolean) => void;
    lastMonitoringDate: string; setLastMonitoringDate: (v: string) => void;
    comments: string; setComments: (v: string) => void;
    description: string; setDescription: (v: string) => void;
    creditOperationId: string; setCreditOperationId: (v: string) => void;
    obligations: any[]; isLoadingObligations: boolean;
    owners: PropertyOwner[];
    addOwner: (o: PropertyOwner) => void;
    removeOwner: (i: number) => void;
    ownerSearch: string; setOwnerSearch: (v: string) => void;
    ownerResults: any[]; isSearchingOwner: boolean; showOwnerDropdown: boolean;
    searchOwner: (query: string) => void; handleSelectOwner: (person: any) => void;
    valuation: PropertyValuationInput | null; setValuation: (v: PropertyValuationInput | null) => void;
    costs: PropertyCost[]; addCost: (c: PropertyCost) => void; removeCost: (i: number) => void;
}


const PropertyForm = ({
    isCreating, errorFields,
    name, setName,
    registrationNumber, setRegistrationNumber,
    registryOffice, setRegistryOffice,
    registryState, setRegistryState,
    propertyType, setPropertyType,
    incraCode, setIncraCode,
    carCode, setCarCode,
    location, setLocation,
    areaValue, setAreaValue,
    areaUnit, setAreaUnit,
    legalRegime, setLegalRegime,
    appraised, setAppraised,
    monitoringEnabled, setMonitoringEnabled,
    lastMonitoringDate, setLastMonitoringDate,
    comments, setComments,
    description, setDescription,
    creditOperationId, setCreditOperationId,
    obligations, isLoadingObligations,
    owners, addOwner, removeOwner,
    ownerSearch, setOwnerSearch,
    ownerResults, isSearchingOwner, showOwnerDropdown,
    searchOwner, handleSelectOwner,
    valuation, setValuation,
    costs, addCost, removeCost,
}: PropertyFormProps) => {
    const [showCostForm, setShowCostForm] = useState(false);
    const [costType, setCostType] = useState('');
    const [costValue, setCostValue] = useState(0);
    const [costDate, setCostDate] = useState('');
    const [costRegNumber, setCostRegNumber] = useState('');
    const [costRegDate, setCostRegDate] = useState('');

    const isRural = propertyType === PropertyType.RURAL;

    const handleAddCost = () => {
        if (!costType || !costValue || !costDate) return;
        addCost({
            costType: costType as any,
            value: costValue,
            costDate,
            registrationNumber: costRegNumber || undefined,
            registrationDate: costRegDate || undefined,
        });
        setCostType(''); setCostValue(0); setCostDate('');
        setCostRegNumber(''); setCostRegDate('');
        setShowCostForm(false);
    };

    const emptyValuation: PropertyValuationInput = { forcedLiquidationValue: 0, valuationDate: '' };

    return (
        <div className="p-4 space-y-6">
            {/* Obrigação (opcional) */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Obrigação</h4>
                <SelectTextField label="Selecione a obrigação" value={creditOperationId}
                    onChange={e => setCreditOperationId(e.target.value)}
                    items={obligations.map((o: any) => ({
                        value: o.creditOperationId,
                        label: `${o.operationNumber} — ${o.assignorName || o.operationCode || ''}`,
                    }))}
                    emptyLabel={isLoadingObligations ? 'Carregando...' : 'Selecione'}
                    errorMessage={getErrorMessage(errorFields, 'creditOperationId')} />
            </section>

            {/* Dados do Imóvel */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Dados do Imóvel</h4>
                <div className="grid grid-cols-3 gap-3">
                    <SelectTextField label="Tipo *" value={propertyType}
                        onChange={(e) => setPropertyType(e.target.value as PropertyTypeValue)}
                        items={PROPERTY_TYPE_OPTIONS}
                        errorMessage={getErrorMessage(errorFields, 'propertyType')} />
                    <TextEdit label="Nome / Identificação" value={name}
                        onChange={e => setName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'name')} />
                    <SelectTextField label="Regime Jurídico *" value={legalRegime}
                        onChange={(e) => setLegalRegime(e.target.value as LegalRegimeValue)}
                        items={LEGAL_REGIME_OPTIONS}
                        errorMessage={getErrorMessage(errorFields, 'legalRegime')} />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Matrícula" value={registrationNumber} helperText="opcional"
                        onChange={e => setRegistrationNumber(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registrationNumber')} />
                    <TextEdit label="Cartório *" value={registryOffice}
                        onChange={e => setRegistryOffice(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registryOffice')} />
                    <SelectTextField label="UF Cartório *" value={registryState}
                        onChange={e => setRegistryState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'registryState')} />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Localização" value={location}
                        onChange={e => setLocation(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'location')} />
                    <TextEdit label="Área" type="number" value={areaValue === '' ? '' : String(areaValue)}
                        onChange={e => setAreaValue(e.target.value === '' ? '' : +e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'areaValue')} />
                    <SelectTextField label="Unidade" value={areaUnit}
                        onChange={(e) => setAreaUnit(e.target.value as AreaUnitValue)}
                        items={AREA_UNIT_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'areaUnit')} />
                </div>

                {/* Toggle rural/urbano — INCRA / CAR apenas para RURAL (Req 12.3) */}
                {isRural && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <TextEdit label="Código INCRA *" value={incraCode}
                            onChange={e => setIncraCode(e.target.value)}
                            errorMessage={getErrorMessage(errorFields, 'incraCode')} />
                        <TextEdit label="Código CAR *" value={carCode}
                            onChange={e => setCarCode(e.target.value)}
                            errorMessage={getErrorMessage(errorFields, 'carCode')} />
                    </div>
                )}

                <div className="grid grid-cols-1 gap-3 mt-3">
                    <TextEdit label="Descrição" value={description}
                        onChange={e => setDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'description')} />
                </div>
            </section>

            {/* Proprietários (múltiplos — Req 12.4) */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Proprietários</h4>
                    <span className="text-xs text-gray-400">{owners.length} / {MAX_OWNERS}</span>
                </div>
                <div className="relative">
                    <TextEdit label="Nome / Razão Social (Enter para buscar)" value={ownerSearch}
                        onChange={e => setOwnerSearch(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && searchOwner(ownerSearch)}
                        errorMessage={getErrorMessage(errorFields, 'ownerName')} />
                    {isSearchingOwner && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showOwnerDropdown && ownerResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {ownerResults.slice(0, 8).map((p: any, idx: number) => (
                                <div key={p.personId || idx} onClick={() => handleSelectOwner(p)}
                                    className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                    <span className="font-medium dark:text-white">{p.name}</span>
                                    {p.documentFormatted && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{p.documentFormatted}</span>}
                                    <span className="text-[10px] text-gray-400 ml-1">({p.type})</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {owners.length > 0 && (
                    <div className="space-y-1 mt-3">
                        {owners.map((o, i) => (
                            <div key={o.personId || i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{o.name}</span>
                                    {o.document && <span className="text-gray-500 dark:text-gray-400">{o.document}</span>}
                                </div>
                                <button type="button" onClick={() => removeOwner(i)}
                                    className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}

                {getErrorMessage(errorFields, 'owners') && owners.length === 0 && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {getErrorMessage(errorFields, 'owners')}
                    </div>
                )}
            </section>

            {/* Monitoramento */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Monitoramento</h4>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={monitoringEnabled}
                        onChange={e => setMonitoringEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Habilitar monitoramento
                </label>
                {monitoringEnabled && (
                    <div className="grid grid-cols-3 gap-3 mt-3">
                        <TextEdit label="Última data de monitoramento" type="date" value={lastMonitoringDate}
                            onChange={e => setLastMonitoringDate(e.target.value)}
                            errorMessage={getErrorMessage(errorFields, 'lastMonitoringDate')} />
                    </div>
                )}
            </section>

            {/* Avaliação (opcional) */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Avaliação (opcional)</h4>
                    <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                        <input type="checkbox" checked={appraised}
                            onChange={e => setAppraised(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        Avaliado
                    </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <CurrencyInput label="Valor de Liquidação Forçada" prefix="R$" fractionDigits={2}
                        value={valuation?.forcedLiquidationValue || 0}
                        onChange={(v) => setValuation({ ...(valuation || emptyValuation), forcedLiquidationValue: v })} />
                    <TextEdit label="Data Avaliação" type="date" value={valuation?.valuationDate || ''}
                        onChange={e => setValuation({ ...(valuation || emptyValuation), valuationDate: e.target.value })} />
                </div>
            </section>

            {/* Custos / Registros (opcional) */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Custos / Registros (opcional)</h4>
                    {!showCostForm && (
                        <button type="button" onClick={() => setShowCostForm(true)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Adicionar</button>
                    )}
                </div>

                {costs.length > 0 && (
                    <div className="space-y-1 mb-3">
                        {costs.map((c, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{COST_TYPE_OPTIONS.find(o => o.value === c.costType)?.label || c.costType}</span>
                                    <span className="text-gray-500">R$ {c.value.toFixed(2)}</span>
                                    <span className="text-gray-400">{c.costDate}</span>
                                    {c.registrationNumber && <span className="text-gray-400">Nº {c.registrationNumber}</span>}
                                </div>
                                <button type="button" onClick={() => removeCost(i)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}

                {showCostForm && (
                    <div className="p-3 border border-gray-200 dark:border-slate-700 rounded-lg space-y-3 bg-gray-50 dark:bg-slate-800/50">
                        <div className="grid grid-cols-3 gap-3">
                            <SelectTextField label="Tipo *" value={costType}
                                onChange={e => setCostType(e.target.value)} items={COST_TYPE_OPTIONS} emptyLabel="Selecione" />
                            <CurrencyInput label="Valor *" prefix="R$" fractionDigits={2} value={costValue}
                                onChange={v => setCostValue(v)} />
                            <TextEdit label="Data *" type="date" value={costDate}
                                onChange={e => setCostDate(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            <TextEdit label="Nº Registro" value={costRegNumber}
                                onChange={e => setCostRegNumber(e.target.value)} />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={() => setShowCostForm(false)}
                                className="px-3 py-1.5 text-sm text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">Cancelar</button>
                            <button type="button" onClick={handleAddCost}
                                className="px-3 py-1.5 text-sm text-white bg-gray-600 rounded-lg hover:bg-gray-700">Adicionar</button>
                        </div>
                    </div>
                )}
            </section>

            {/* Comentários */}
            <section>
                <TextEdit label="Comentários" value={comments}
                    onChange={e => setComments(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'comments')} />
            </section>
        </div>
    );
};

export default PropertyForm;
